import React, { useEffect, useState } from "react";
import Tooltip from "./Tooltip";
import PhysicsOutlineLockBadge from "./PhysicsOutlineLockBadge";

const NEUTRAL_ICON_HOVER_BG = "bg-[var(--panel-surface-hover)]";
const NEUTRAL_ICON_HOVER_BG_CLASS = "hover:bg-[var(--panel-surface-hover)]";

/** True when the primary pointer can hover (avoids touch long-press → synthetic mouseenter opening submenus under the finger). */
function usePrimaryInputSupportsHoverMenu() {
  const [supports, setSupports] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(hover: hover) and (pointer: fine)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setSupports(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return supports;
}

const IconActionContextRow = ({
  actions = [],
  activeSubmenuKey = null,
  setActiveSubmenuKey,
  onMouseLeave,
  itemRole = undefined,
  submenuRole = undefined,
  submenuClassName = "absolute left-0 top-full mt-1 rounded-md border border-gray-600 p-1 shadow-xl z-[50000] isolate",
  submenuStyle = { backgroundColor: "rgba(40, 40, 40, 0.95)" },
}) => {
  const hoverOpensSubmenu = usePrimaryInputSupportsHoverMenu();
  const activeAction = actions.find(
    (action) => action?.key === activeSubmenuKey && !!action?.submenu && !action?.disabled
  );

  // `onMouseLeave` schedules submenu close on desktop (pointer moved away). Touch stacks synthesize
  // mouseleave/out around taps, which would collapse the submenu before the next tap lands.
  return (
    <div className="relative" onMouseLeave={hoverOpensSubmenu ? onMouseLeave : undefined}>
      <div className="flex items-center gap-1">
        {actions.map((action) => {
          const {
            key,
            label,
            icon,
            disabled = false,
            disabledByPhysics = false,
            submenu = null,
            onClick,
            onDefaultClick,
            onHover,
            buttonClassName = "",
          } = action;
          const isDisabled = disabled;
          const isActive = !isDisabled && !!submenu && activeSubmenuKey === key;
          const tooltipContent = disabledByPhysics && isDisabled ? "Stop physics to edit." : label;
          return (
            <Tooltip
              key={key}
              content={<span className="whitespace-nowrap">{tooltipContent}</span>}
            >
              <button
                type="button"
                disabled={isDisabled}
                role={itemRole}
                aria-label={label}
                aria-haspopup={submenu ? "menu" : undefined}
                aria-expanded={submenu ? isActive : undefined}
                onClick={() => {
                  if (isDisabled) return;
                  // Desktop: hover reveals submenu; primary click may run a default (e.g. quick add/delete).
                  // Touch/coarse: no hover — opening the submenu must come first, not onDefaultClick.
                  const preferSubmenuTap = !hoverOpensSubmenu && !!submenu && !!setActiveSubmenuKey;
                  if (preferSubmenuTap) {
                    setActiveSubmenuKey(isActive ? null : key);
                    if (onClick) onClick();
                    return;
                  }
                  if (onDefaultClick) {
                    onDefaultClick();
                    return;
                  }
                  if (submenu && setActiveSubmenuKey) {
                    setActiveSubmenuKey(isActive ? null : key);
                  }
                  if (onClick) onClick();
                }}
                onMouseEnter={() => {
                  if (!hoverOpensSubmenu || isDisabled) return;
                  if (onHover) onHover();
                  if (submenu && setActiveSubmenuKey) {
                    setActiveSubmenuKey(key);
                  }
                }}
                onFocus={() => {
                  if (isDisabled) return;
                  if (onHover) onHover();
                  if (submenu && setActiveSubmenuKey) {
                    setActiveSubmenuKey(key);
                  } else if (setActiveSubmenuKey) {
                    setActiveSubmenuKey(null);
                  }
                }}
                className={`relative h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                  isDisabled
                    ? disabledByPhysics
                      ? "border border-amber-400/80 bg-amber-500/10 text-amber-200 cursor-not-allowed shadow-[0_0_0_1px_rgba(251,191,36,0.12)] disabled:pointer-events-auto"
                      : "text-gray-300 opacity-60 cursor-not-allowed"
                    : isActive
                      ? `text-white ${NEUTRAL_ICON_HOVER_BG}`
                      : `text-gray-200 ${NEUTRAL_ICON_HOVER_BG_CLASS}`
                } ${buttonClassName}`}
              >
                {icon}
                {disabledByPhysics && isDisabled ? <PhysicsOutlineLockBadge /> : null}
              </button>
            </Tooltip>
          );
        })}
      </div>

      {activeAction ? (
        <div
          role={submenuRole}
          className={submenuClassName}
          style={submenuStyle}
          onMouseEnter={() => {
            if (!hoverOpensSubmenu || !setActiveSubmenuKey) return;
            setActiveSubmenuKey(activeAction.key);
          }}
        >
          {activeAction.submenu}
        </div>
      ) : null}
    </div>
  );
};

export default IconActionContextRow;
